import React, { useEffect, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import axios from 'axios'
import { toast } from 'react-toastify'
import { Activity, Search } from 'lucide-react'
import { FilterCard } from '../ui'

const API_BASE = process.env.REACT_APP_API_URL || '/api'

const statusConfig = {
  CHECKED_IN: {
    label: 'Checked In',
    bg: 'bg-green-100',
    text: 'text-green-800',
    border: 'border-green-300',
    dot: 'bg-green-500',
  },
  CHECKED_OUT: {
    label: 'Checked Out',
    bg: 'bg-gray-100',
    text: 'text-gray-600',
    border: 'border-gray-300',
    dot: 'bg-gray-400',
  },
  NOT_CHECKED_IN: {
    label: 'Not Checked In',
    bg: 'bg-yellow-50',
    text: 'text-yellow-700',
    border: 'border-yellow-200',
    dot: 'bg-yellow-400',
  },
}

const formatTime = (isoStr) => {
  if (!isoStr) return '—'
  return new Date(isoStr).toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
}

const PlayerCheckInList = () => {
  const [players, setPlayers] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('ALL')
  const [search, setSearch] = useState('')
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [lastUpdated, setLastUpdated] = useState(null)
  const [checkingOut, setCheckingOut] = useState(null) // userId being checked out
  const [confirmCheckOut, setConfirmCheckOut] = useState(null) // player to confirm

  const adminLogin = useSelector((state) => state.adminLogin || {})
  const { userInfo } = adminLogin

  const fetchCheckIns = useCallback(async () => {
    if (!userInfo?.token) return
    try {
      setLoading(true)
      const { data } = await axios.get(`${API_BASE}/users/today-checkins`, {
        headers: { Authorization: `Bearer ${userInfo.token}` },
      })
      if (data.success) {
        setPlayers(data.players || [])
        setLastUpdated(new Date())
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load check-in list')
    } finally {
      setLoading(false)
    }
  }, [userInfo?.token])

  useEffect(() => {
    fetchCheckIns()
    const interval = setInterval(fetchCheckIns, 30000)
    return () => clearInterval(interval)
  }, [fetchCheckIns])

  const handleCheckOut = async (player) => {
    if (!player?.userId) return
    try {
      setCheckingOut(player.userId)
      await axios.post(
        `${API_BASE}/users/${player.userId}/manual-checkout`,
        {},
        { headers: { Authorization: `Bearer ${userInfo.token}` } }
      )
      toast.success(`${player.name} checked out — they will be redirected to login`)
      setConfirmCheckOut(null)
      await fetchCheckIns()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Check-out failed')
    } finally {
      setCheckingOut(null)
    }
  }

  const filtered = players.filter((p) => {
    const matchesFilter = filter === 'ALL' || p.status === filter
    const matchesSearch =
      !search ||
      p.name?.toLowerCase().includes(search.toLowerCase()) ||
      p.identifier?.toLowerCase().includes(search.toLowerCase())
    return matchesFilter && matchesSearch
  })

  const counts = {
    ALL: players.length,
    CHECKED_IN: players.filter((p) => p.status === 'CHECKED_IN').length,
    CHECKED_OUT: players.filter((p) => p.status === 'CHECKED_OUT').length,
  }

  return (
    <div className="w-full">
      {/* Confirm Modal */}
      {confirmCheckOut && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Confirm Check-Out</h3>
            <p className="text-gray-600 text-sm mb-1">
              You are about to check out <strong>{confirmCheckOut.name}</strong> ({confirmCheckOut.identifier}).
            </p>
            <p className="text-gray-500 text-sm mb-5">
              Their session will be ended and they will be redirected to the login screen.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConfirmCheckOut(null)}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleCheckOut(confirmCheckOut)}
                disabled={checkingOut === confirmCheckOut.userId}
                className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 font-medium flex items-center gap-2"
              >
                {checkingOut === confirmCheckOut.userId ? (
                  <>
                    <span className="animate-spin h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full" />
                    Checking out...
                  </>
                ) : (
                  'Check Out'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl sm:text-[26px] font-extrabold m-0">Check-in list</h1>
          <p className="text-sm text-gray-500 mt-1">
            Today's attendance —{' '}
            {lastUpdated
              ? `Last updated ${formatTime(lastUpdated.toISOString())}`
              : 'Loading...'}
          </p>
        </div>
        <button
          onClick={fetchCheckIns}
          disabled={loading}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
        >
          {loading ? (
            <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          )}
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 text-center">
          <div className="text-3xl font-bold text-blue-600">{counts.ALL}</div>
          <div className="text-sm text-gray-500 mt-1">Total Scanned</div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-green-100 p-4 text-center">
          <div className="text-3xl font-bold text-green-600">{counts.CHECKED_IN}</div>
          <div className="text-sm text-gray-500 mt-1">Currently In</div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 text-center">
          <div className="text-3xl font-bold text-gray-500">{counts.CHECKED_OUT}</div>
          <div className="text-sm text-gray-500 mt-1">Checked Out</div>
        </div>
      </div>

      {/* Filters + Search */}
      <FilterCard
        id="checkin-filters"
        className="mb-4"
        inlineToggle
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        chips={[
          filter !== 'ALL' && { icon: Activity, label: filter === 'CHECKED_IN' ? 'Checked In' : 'Checked Out' },
          search.trim() && { icon: Search, label: `"${search.trim()}"` },
        ].filter(Boolean)}
        emptyLabel="All players"
      >
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex gap-2">
          {['ALL', 'CHECKED_IN', 'CHECKED_OUT'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filter === f
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f === 'ALL' ? 'All' : f === 'CHECKED_IN' ? 'Checked In' : 'Checked Out'}
              <span className="ml-1.5 bg-white bg-opacity-30 rounded-full px-1.5 text-xs">
                {f === 'ALL'
                  ? counts.ALL
                  : f === 'CHECKED_IN'
                  ? counts.CHECKED_IN
                  : counts.CHECKED_OUT}
              </span>
            </button>
          ))}
        </div>
        <input
          type="text"
          placeholder="Search by name or ID..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="ml-auto border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 min-w-[220px]"
        />
      </div>
      </FilterCard>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading && players.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <svg
              className="w-12 h-12 mx-auto mb-3 opacity-40"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <p className="text-sm">No players found for today</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-5 py-3 text-gray-500 font-semibold">S.No.</th>
                <th className="text-left px-5 py-3 text-gray-500 font-semibold">Player</th>
                <th className="text-left px-5 py-3 text-gray-500 font-semibold">ID</th>
                <th className="text-left px-5 py-3 text-gray-500 font-semibold">Status</th>
                <th className="text-left px-5 py-3 text-gray-500 font-semibold">Check-In</th>
                <th className="text-left px-5 py-3 text-gray-500 font-semibold">Check-Out</th>
                <th className="text-left px-5 py-3 text-gray-500 font-semibold">Club</th>
                <th className="text-left px-5 py-3 text-gray-500 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map((player, idx) => {
                const cfg = statusConfig[player.status] || statusConfig.NOT_CHECKED_IN
                const isCheckedIn = player.status === 'CHECKED_IN'
                const isBeingCheckedOut = checkingOut === player.userId

                return (
                  <tr key={player.userId || idx} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-3 text-gray-400">{idx + 1}</td>
                    <td className="px-5 py-3 font-medium text-gray-800">{player.name || '—'}</td>
                    <td className="px-5 py-3 text-gray-500 font-mono text-xs">
                      {player.identifier || '—'}
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg.bg} ${cfg.text} ${cfg.border}`}
                      >
                        <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
                        {cfg.label}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-gray-600">{formatTime(player.latestCheckIn)}</td>
                    <td className="px-5 py-3 text-gray-600">{formatTime(player.latestCheckOut)}</td>
                    <td className="px-5 py-3 text-gray-500 text-xs">
                      {player.club?.name || player.club?.clubCode || '—'}
                    </td>
                    <td className="px-5 py-3">
                      {isCheckedIn ? (
                        <button
                          onClick={() => setConfirmCheckOut(player)}
                          disabled={isBeingCheckedOut}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 text-xs font-semibold disabled:opacity-50 transition-colors"
                        >
                          {isBeingCheckedOut ? (
                            <span className="animate-spin h-3 w-3 border-2 border-red-500 border-t-transparent rounded-full" />
                          ) : (
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                          )}
                          Check Out
                        </button>
                      ) : (
                        <span className="text-gray-300 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

export default PlayerCheckInList
